import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import {
  moodleSyncService, MoodleSyncAction, MoodleSyncJob, MoodleSyncJobName, MoodleSyncRun, MoodleSyncStatus,
} from '../../services/moodleSyncService';

const POLL_INTERVAL_MS = 10_000;
const BUSY_POLL_INTERVAL_MS = 2_000; // tâche en cours ou demandée : suivi rapproché des étapes

const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const formatDuration = (fromIso: string, toIso: string) => {
  const seconds = Math.max(0, Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000));
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
};

const OUTCOME: Record<MoodleSyncRun['outcome'], { label: string; color: 'success' | 'error' | 'warning' }> = {
  ok: { label: 'Réussi', color: 'success' },
  no_token: { label: "Token d'API absent", color: 'error' },
  session_expired: { label: 'Session Moodle expirée', color: 'error' },
  error: { label: 'En échec (voir les alertes)', color: 'error' },
};

const JOB_DESCRIPTIONS: Record<MoodleSyncJobName, (job: MoodleSyncJob) => string> = {
  imports: (job) => `Pousse dans Moodle les notes « Auto-inscription », « Composition groupe », « PREPA.TS1 » et `
    + `« PREPA.TS2 » toutes les ${job.interval_min ?? 10} minutes ; seuls les fichiers modifiés sont importés.`,
  stats: (job) => `Lit dans Moodle (sans rien y écrire) le carnet de notes, l'achèvement et le journal du cours `
    + `toutes les ${job.interval_hours ?? 12} h, pour la page « Suivi Moodle ».`,
};

// Clés d'alerte du worker -> tâche concernée (null : commun aux deux) et libellé lisible.
const alertJob = (key: string): MoodleSyncJobName | null => {
  if (key === 'session' || key === 'api_token') return null;
  if (key.startsWith('stats') || key === 'cycle_stats') return 'stats';
  return 'imports';
};

const alertLabel = (key: string, status: MoodleSyncStatus): string => {
  if (key === 'session') return 'Session Moodle expirée';
  if (key === 'api_token') return "Token d'API refusé par pwa-dashboard";
  if (key === 'api') return 'API pwa-dashboard en erreur : CSV non téléchargés';
  if (key === 'cycle') return 'Import interrompu par une erreur (voir les journaux du worker)';
  if (key === 'stats') return 'Collecte en échec (voir les journaux du worker)';
  if (key === 'cycle_stats') return 'Collecte interrompue par une erreur (voir les journaux du worker)';
  if (key === 'stats_columns') {
    const missing = status.jobs.stats.last_run?.summary?.missing_columns ?? [];
    return 'Activité(s) introuvable(s) dans les exports Moodle, sans doute renommée(s)'
      + (missing.length ? ` : ${missing.join(', ')}` : '');
  }
  if (key.startsWith('import_')) return `Import Moodle en échec : ${key.slice('import_'.length)}`;
  if (key.startsWith('empty_')) return `CSV vide, import ignoré : ${key.slice('empty_'.length)}`;
  if (key.startsWith('unknown_')) return `Étudiant(s) absent(s) du cours Moodle : ${key.slice('unknown_'.length)}`;
  return key;
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    {children}
  </Box>
);

interface JobCardProps {
  name: MoodleSyncJobName;
  status: MoodleSyncStatus;
  acting: string | null;
  onAction: (job: MoodleSyncJobName, action: MoodleSyncAction) => void;
}

// Une tâche du worker : état, dernier et prochain passage, alertes, boutons (grisés dès qu'une tâche tourne).
const JobCard: React.FC<JobCardProps> = ({ name, status, acting, onAction }) => {
  const job = status.jobs[name];
  const isRunning = status.running?.job === name;
  const busy = status.running !== null || acting !== null;
  const state = isRunning
    ? { label: 'En cours', color: 'info' as const }
    : job.requested
      ? { label: 'Demandée, démarrage imminent', color: 'info' as const }
      : job.paused
        ? { label: 'En pause', color: 'warning' as const }
        : { label: 'Active', color: 'success' as const };
  const last = job.last_run;
  const alerts = Object.entries(status.alerts).filter(([key]) => alertJob(key) === name);
  const summary = last?.summary;

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h6">{job.label}</Typography>
        <Typography variant="body2" color="text.secondary">{JOB_DESCRIPTIONS[name](job)}</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Field label="État">
          <Chip label={state.label} color={state.color}
            icon={isRunning || job.requested ? <CircularProgress size={14} color="inherit" /> : undefined} />
        </Field>
        <Field label="Dernier passage">
          {last ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography>{formatDate(last.finished_at)}</Typography>
              <Chip size="small" label={OUTCOME[last.outcome].label} color={OUTCOME[last.outcome].color}
                variant="outlined" />
              <Typography variant="body2" color="text.secondary">
                ({formatDuration(last.started_at, last.finished_at)})
              </Typography>
            </Box>
          ) : <Typography>—</Typography>}
        </Field>
        {name === 'stats' && (
          <Field label="Dernière collecte réussie">
            <Typography>{formatDate(job.last_success_at)}</Typography>
          </Field>
        )}
        <Field label="Prochain passage">
          <Typography>{isRunning ? 'en cours' : job.paused ? '— (en pause)' : formatDate(job.next_at)}</Typography>
        </Field>
      </Box>

      {isRunning && status.running && (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {status.running.step ?? 'Démarrage…'} — depuis {formatDuration(status.running.started_at, status.now)}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {name === 'stats' && summary && last?.outcome === 'ok' && (
        <Typography variant="body2" color="text.secondary">
          Bilan du dernier passage : {summary.participants ?? '?'} étudiant·es rapproché·es, {summary.grades ?? '?'} notes,{' '}
          {summary.daily_rows ?? '?'} lignes d'activité.
        </Typography>
      )}

      {alerts.length > 0 && (
        <Alert severity="error">
          {alerts.map(([key, since]) => (
            <Typography key={key} variant="body2">{alertLabel(key, status)} (depuis le {formatDate(since)})</Typography>
          ))}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={() => onAction(name, 'run-now')}
          disabled={busy || job.requested}>
          {acting === `${name}/run-now` ? <CircularProgress size={20} /> : 'Lancer maintenant'}
        </Button>
        {job.paused ? (
          <Button variant="outlined" onClick={() => onAction(name, 'resume')} disabled={busy}>
            {acting === `${name}/resume` ? <CircularProgress size={20} /> : 'Reprendre'}
          </Button>
        ) : (
          <Button variant="outlined" color="warning" onClick={() => onAction(name, 'pause')} disabled={busy}>
            {acting === `${name}/pause` ? <CircularProgress size={20} /> : 'Mettre en pause'}
          </Button>
        )}
      </Box>

      {name === 'imports' && job.items && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Note Moodle</TableCell>
                <TableCell>Dernier import (contenu modifié)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(job.items).map(([item, info]) => (
                <TableRow key={item}>
                  <TableCell>{info.label}</TableCell>
                  <TableCell>{formatDate(info.last_import_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

const ACTION_NOTICES: Record<MoodleSyncAction, string> = {
  'run-now': 'demandée : démarrage dans quelques secondes.',
  pause: 'mise en pause.',
  resume: 'reprise : un passage démarre dans quelques secondes.',
};

// Pilote le worker moodle-sync (conteneur interne) : deux tâches indépendantes (import des notes, collecte du
// suivi), chacune avec pause, reprise et lancement immédiat. Toutes les actions passent par l'API, qui relaie
// au worker ; la page se rafraîchit seule (toutes les 2 s pendant qu'une tâche tourne, sinon toutes les 10 s).
const AdminMoodleSyncPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';

  const [status, setStatus] = useState<MoodleSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      setStatus(await moodleSyncService.getStatus(token));
      setError(null);
    } catch (e: any) {
      setStatus(null);
      setError(e.message || "Impossible de lire l'état de la synchronisation");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = !!status && (status.running !== null || Object.values(status.jobs).some((job) => job.requested));

  useEffect(() => {
    if (!isAdmin) return undefined;
    load();
    const timer = window.setInterval(load, busy ? BUSY_POLL_INTERVAL_MS : POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isAdmin, load, busy]);

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }

  const handleAction = async (job: MoodleSyncJobName, action: MoodleSyncAction) => {
    setActing(`${job}/${action}`);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const next = await moodleSyncService.act(job, action, token);
      setStatus(next);
      setNotice(`${next.jobs[job].label} : ${ACTION_NOTICES[action]}`);
      setError(null);
    } catch (e: any) {
      setNotice(null);
      setError(e.message || "Erreur lors de l'action");
    } finally {
      setActing(null);
    }
  };

  const session = status && {
    ok: { label: 'Valide', color: 'success' as const },
    expired: { label: 'Expirée', color: 'error' as const },
    unknown: { label: 'Pas encore vérifiée', color: 'default' as const },
  }[status.session];
  const api = status && {
    ok: { label: 'Accepté', color: 'success' as const },
    token_refused: { label: 'Refusé', color: 'error' as const },
    unknown: { label: 'Pas encore vérifié', color: 'default' as const },
  }[status.api];
  const commonAlerts = status ? Object.entries(status.alerts).filter(([key]) => alertJob(key) === null) : [];
  const running = status?.running ? status.jobs[status.running.job] : null;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h4">Synchronisation Moodle</Typography>

      {error && <Alert severity="warning">{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      {loading && <CircularProgress size={28} />}

      {status && session && api && (
        <>
          <Paper sx={{ p: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Field label="Activité du worker">
              {running ? (
                <Chip color="info" label={`En cours : ${running.label}`}
                  icon={<CircularProgress size={14} color="inherit" />} />
              ) : (
                <Chip label="Au repos, en attente du prochain passage" />
              )}
            </Field>
            <Field label="Session Moodle"><Chip label={session.label} color={session.color} /></Field>
            <Field label="Token d'API"><Chip label={api.label} color={api.color} /></Field>
          </Paper>

          {running && (
            <Alert severity="info">
              Une tâche est en cours : les boutons sont désactivés jusqu'à la fin. Les deux tâches partagent le même
              navigateur Moodle et s'exécutent l'une après l'autre.
            </Alert>
          )}

          {commonAlerts.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2">Problèmes en cours (les deux tâches)</Typography>
              {commonAlerts.map(([key, since]) => (
                <Typography key={key} variant="body2">{alertLabel(key, status)} (depuis le {formatDate(since)})</Typography>
              ))}
            </Alert>
          )}

          {status.session === 'expired' && (
            <Alert severity="info">
              Pour renouveler la session : depuis votre poste, lancez{' '}
              <code>python3 script_python/renew_moodle_session.py --run-cycle</code>. La connexion CAS et le code
              MFA se font dans une fenêtre de navigateur ; les imports reprennent ensuite seuls.
            </Alert>
          )}

          <JobCard name="imports" status={status} acting={acting} onAction={handleAction} />
          <JobCard name="stats" status={status} acting={acting} onAction={handleAction} />
        </>
      )}
    </Box>
  );
};

export default AdminMoodleSyncPage;
